import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { makeApiCall } from "src/api/apiRequests";
import * as ConfirmPopup from "../../../helpers/ConfirmPopup";
import ValidationMessage from "src/helpers/ValidationMessage";
import CustomPagination from "src/helpers/CustomPaginate";

import EditNursery from "./EditNursery";

import API_PATHS from "src/api/apiPaths";
import PATHS from "src/routes/Paths";
import API_HEADERS from "src/api/apiConfig";
import SearchFilter from "src/helpers/SearchFilter";
import ListResponse from "src/helpers/ListResponse";
import FLASH_MESSAGES from "src/helpers/FlashMessages";

import Loading from "src/helpers/Loading";
import { PermissionCheck } from "src/helpers/PermissionCheck";

const NurseryGrid: React.FC<{
  text: string;
  specification: string;
  filter: string;
  searched: boolean;
  setSearch: (visible: boolean) => void;
}> = ({ text, specification, searched, filter, setSearch }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [type, setType] = useState("cerdhe");
  const [pagination, setPagination] = useState("yes");

  const [nurseries, setNurseries] = useState<any>([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState("list");
  const [createNurseryModal, setCreateNurseryModal] = useState(false);
  const [editNurseryModal, setEditNurseryModal] = useState(false);
  const [modalId, setModalId] = useState<number | null>(null);
  const [modified, setModified] = useState(false);
  const [btnLoading, setBtnLoading] = useState(false);
  const [paginate, setPaginate] = useState<any>([]);
  const [activePage, setActivePage] = useState<number>(1);
  const [validations, setValidations] = useState<Record<string, string>>({});

  const sortingOptions = [
    { value: "newest_first", label: "Sort By: Newest First" },
    { value: "oldest_first", label: "Sort By: Oldest First" },
  ];

  const [selectedOption, setSelectedOption] = useState(sortingOptions[0]);

  const getNurseries = async (hasSearch: any | undefined): Promise<void> => {
    setModified(false);

    if (hasSearch) {
      hasSearch.preventDefault();
      setBtnLoading(true);
    }

    let request: any = API_PATHS.listKindergartens;

    var searchParams: any = {
      type: type,
      pagination: pagination,
      filter: filter,
    };

    if (searched || (text && specification)) {
      if (text) {
        searchParams["text"] = text;
      }
      if (specification) {
        searchParams["specification"] = specification;
      }
    }

    if (activePage > 1) {
      searchParams["page"] = activePage;
    }

    request = SearchFilter(searchParams, request);

    try {
      const response: any = await makeApiCall<ResponseType>(
        request,
        "GET",
        API_HEADERS.authenticated
      );
      setValidations({});
      setSearch(false);
      setNurseries(response.kindergartens.data);
      setPaginate(response.kindergartens);
      setBtnLoading(false);
      setLoading(false);
    } catch (error: any) {
      FLASH_MESSAGES.errorMsg(error.response.data.error);
      navigate(-1);
    }
  };

  useEffect(() => {
    getNurseries("");
  }, [modified, activePage, filter]);

  useEffect(() => {
    searched && getNurseries("");
  }, [searched]);

  const deleteKindergarten = (kindergartenId: number, nurseryTitle: string) => {
    ConfirmPopup.deleteNurseryPopup(
      t("delete_nursery_title"),
      t("delete_nursery_type"),
      nurseryTitle
    ).then(async (response) => {
      if (response.isConfirmed) {
        try {
          const response: any = await makeApiCall<ResponseType>(
            API_PATHS.deleteKindergarten(kindergartenId),
            "DELETE",
            API_HEADERS.authenticated
          );

          setModified(true);
          FLASH_MESSAGES.successMsg(response.success);
        } catch (error: any) {
          if (error.response.data.error) {
            FLASH_MESSAGES.errorMsg(error.response.data.error);
          }
        }
      }
    });
  };

  return (
    <div>
      <div className="grid-container">
        {loading ? (
          <tr>
            <td className="text-center" colSpan={6}>
              <Loading />
            </td>
          </tr>
        ) : (
          ""
        )}
        {nurseries.map((nursery: any) => (
          <div className="grid-item" key={nursery.id}>
            <div className="grid-header">
              <div className="grid-number">
                {nursery.number ? nursery.number : "-"}
              </div>
              {nursery.name ? nursery.name : "-"}
            </div>
            <div className="grid-content">
              <div className="grid-title mt-2">
                <i className="icon address-icon" />
                {t("nursery_address")}
              </div>
              <div className="grid-subtitle">
                {nursery.address ? nursery.address : "-"}
              </div>
              <div className="grid-title">
                <i className="icon telephone-icon" />
                {t("nursery_mobile")}
              </div>
              <div className="grid-subtitle">
                {nursery.phone ? (
                  <a href={`tel:${nursery.phone}`}>{nursery.phone}</a>
                ) : (
                  "-"
                )}
              </div>
              <div className="grid-title">
                <i className="icon email-icon" />
                {t("nursery_email")}
              </div>
              <div className="grid-subtitle text-blue">
                {nursery.email ? (
                  <a href={`mailto:${nursery.email}`}>{nursery.email}</a>
                ) : (
                  "-"
                )}
              </div>
              <div className="grid-title">
                <i className="icon website-icon" />
                {t("nursery_website")}
              </div>
              <div className="grid-subtitle">
                {nursery.website ? (
                  <a
                    href={nursery.website}
                    className="text-decoration-none"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {nursery.website}
                  </a>
                ) : (
                  "-"
                )}
              </div>
            </div>
            <div className="grid-actions">
              <div className="mt-2">
                {PermissionCheck("kindergarten-update") ? (
                  <button
                    type="button"
                    className="button-unstyled-icon"
                    onClick={(e) => {
                      setEditNurseryModal(true);
                      setModalId(nursery.id);
                      e.stopPropagation();
                    }}
                  >
                    <i className="icon edit-icon" />
                  </button>
                ) : (
                  "-"
                )}

                {editNurseryModal && modalId === nursery.id && (
                  <EditNursery
                    onShowModal={editNurseryModal}
                    setNurseryVisible={(e) => {
                      setEditNurseryModal(e);
                      setModalId(null);
                    }}
                    kindergartenId={nursery.id}
                    setModified={(e) => setModified(e)}
                  />
                )}
                {PermissionCheck("kindergarten-show") ? (
                  <button
                    type="button"
                    className="button-unstyled-icon"
                    onClick={() => navigate(PATHS.showNursery + nursery.id)}
                  >
                    <i className="icon show-icon" />
                  </button>
                ) : (
                  "-"
                )}
                {PermissionCheck("kindergarten-delete") ? (
                  <button
                    className="button-unstyled-icon"
                    onClick={(e) => {
                      deleteKindergarten(nursery.id, nursery.name);
                    }}
                  >
                    <i className="icon delete-icon" />
                  </button>
                ) : (
                  "-"
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
      <ListResponse
        totalItems={nurseries.length}
        loading={loading}
        searched={searched}
      />
      <CustomPagination
        data={paginate}
        setActivePage={(e) => {
          setActivePage(e);
          Object.keys(validations).length > 0 && setSearch(false);
        }}
      />
    </div>
  );
};
export default NurseryGrid;
